/**
 * Library — Beatly's curated, cleared catalog (public /studio/v1/library + /studio/v1/playlists), ported from the Sound
 * Ad-Studio SPA: search, genre / mood / tempo / type filters, featured playlists, track list, add to beat, download.
 * On the Audio Studio page it is the "Open library" view of the Library tab (always expanded, paginated). Given
 * `onExpandedChange` it can instead start collapsed to a short preview until "Browse all".
 * `publicMode` (the no-login /library page creators open from a brief): browse, play and download only — no
 * "Add to beat" and no AttachToCampaignDialog, whose hooks call logged-in APIs. Titles link to the track's public page.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Library, ListMusic, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import {
  downloadAudio,
  libraryAudioUrl,
  libraryCoverUrl,
  libraryDurationSeconds,
  libraryTrackKey,
  libraryTrackTitle,
  playlistCoverUrl,
  type LibraryPlaylist,
  type LibraryTrack,
} from '@/api/audioStudioApi';
import { PlayButton } from './AudioPlayer';
import { AttachToCampaignDialog } from './AttachToCampaignDialog';
import { GenreTile } from './CoverArt';
import type { AttachableTrack } from './attachPayload';
import { capitalize, formatClock, libraryAttachable, libraryPlayable } from './format';
import { useLibraryFilterOptions, useLibraryTracks, usePlaylist, usePlaylists } from './queries';

const PAGE_SIZE = 20;
/** Tracks shown while the section is collapsed. */
const PREVIEW_SIZE = 5;
const selectCls = 'h-9 rounded-[12px] border border-[#eaecf0] bg-white px-2.5 text-[13px] text-[#344054] focus:border-[#d0d5dd] focus:outline-none';

export function LibraryTrackRow({ t, onAttach, action, linkTitle = false }: { t: LibraryTrack; onAttach?: (a: AttachableTrack) => void; action?: React.ReactNode; /** Title links to the public track page (/sound/:id). */ linkTitle?: boolean }) {
  const m = t.csv_metadata || {};
  const secs = libraryDurationSeconds(t);
  const attachable = libraryAttachable(t);
  const audio = libraryAudioUrl(t);
  return (
    <li className="flex min-h-14 items-center gap-3 px-3 py-2 hover:bg-[#fcfcfd] sm:px-4">
      <PlayButton item={libraryPlayable(t)} coverUrl={libraryCoverUrl(t)} fallback={<GenreTile genre={m.genre} title={libraryTrackTitle(t)} instrumental={t.music_type === 'Instrumental'} size="thumb" />} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>
          {linkTitle && t.track_id
            ? <a href={`/sound/${encodeURIComponent(t.track_id)}`} target="_blank" rel="noreferrer" className="hover:underline">{libraryTrackTitle(t)}</a>
            : libraryTrackTitle(t)}
        </div>
        <div className="truncate text-[12px] text-[#667085]">
          {t.artist || 'Beatly Studios'}
          <span className="md:hidden">{[m.genre, capitalize(m.mood)].filter(Boolean).map((x) => ` · ${x}`).join('')}</span>
        </div>
      </div>
      <div className="hidden w-28 shrink-0 truncate text-[12.5px] text-[#475467] md:block">{m.genre || '—'}</div>
      <div className="hidden w-28 shrink-0 truncate text-[12.5px] text-[#475467] lg:block">{capitalize(m.mood) || '—'}</div>
      <div className="hidden w-16 shrink-0 text-[12.5px] text-[#475467] lg:block">{capitalize(m.tempo) || '—'}</div>
      <div className="hidden w-12 shrink-0 text-right text-[12px] tabular-nums text-[#667085] sm:block">{secs != null ? formatClock(secs) : '—'}</div>
      <div className="flex shrink-0 items-center gap-1">
        {action ?? (
          <>
            {onAttach && (
              <Button size="sm" variant="outline" disabled={!attachable} onClick={() => attachable && onAttach(attachable)} className="h-8 gap-1 rounded-[12px] border-[#eaecf0] text-[12px]" aria-label={`Add ${libraryTrackTitle(t)} to a beat`}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> <span className="hidden sm:inline">Add to beat</span>
              </Button>
            )}
            <button
              type="button"
              disabled={!audio}
              onClick={() => { if (audio) void downloadAudio(audio, libraryTrackTitle(t)).catch((e: Error) => toast.error(e.message)); }}
              aria-label={`Download ${libraryTrackTitle(t)}`}
              className="flex h-8 w-8 items-center justify-center rounded-md text-[#667085] hover:bg-[#f2f4f7] disabled:opacity-40"
            >
              <Download className="h-4 w-4" aria-hidden />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function PlaylistsRow({ playlists, selected, onSelect }: { playlists: LibraryPlaylist[]; selected: string | null; onSelect: (id: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * Math.round(ref.current.clientWidth * 0.85), behavior: 'smooth' });
  return (
    <div className="group/scroller relative">
      <button type="button" aria-label="Scroll playlists left" onClick={() => scroll(-1)} className="absolute left-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#eaecf0] bg-white hover:bg-[#f2f4f7] group-hover/scroller:flex"><ChevronLeft className="h-4 w-4" aria-hidden /></button>
      <button type="button" aria-label="Scroll playlists right" onClick={() => scroll(1)} className="absolute right-0 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#eaecf0] bg-white hover:bg-[#f2f4f7] group-hover/scroller:flex"><ChevronRight className="h-4 w-4" aria-hidden /></button>
      <div ref={ref} className="-mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {playlists.map((p) => {
          const cover = playlistCoverUrl(p);
          const n = p.track_count ?? p.track_ids?.length ?? 0;
          const on = selected === p.playlist_id;
          return (
            <button
              key={p.playlist_id}
              type="button"
              onClick={() => onSelect(on ? null : p.playlist_id || null)}
              aria-pressed={on}
              className={cn('group w-[150px] shrink-0 snap-start overflow-hidden rounded-[12px] border bg-white text-left transition-colors sm:w-[180px]', on ? 'border-[#344054]' : 'border-[#eaecf0] hover:border-[#d0d5dd]')}
            >
              <div className="relative aspect-square overflow-hidden bg-[#f2f4f7]">
                {cover ? <img src={cover} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <GenreTile title={p.name} />}
              </div>
              <div className="p-3">
                <div className="line-clamp-1 text-[13px] text-[#101828]" style={{ fontWeight: 600 }}>{p.name || 'Untitled'}</div>
                <div className="text-[11.5px] text-[#667085]">{n} {n === 1 ? 'track' : 'tracks'}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function LibrarySection({ expanded: expandedProp = true, onExpandedChange, initialPlaylistId = null, publicMode = false }: {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Open with this playlist selected (`?playlist=` — old adstudios.beatly.com playlist links land here). */
  initialPlaylistId?: string | null;
  /** No login (the public /library page): no attach UI and no logged-in API calls. */
  publicMode?: boolean;
}) {
  // Without a way to collapse it, the library is always shown in full.
  const expanded = onExpandedChange ? expandedProp : true;
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('');
  const [mood, setMood] = useState('');
  const [tempo, setTempo] = useState('');
  const [musicType, setMusicType] = useState('');
  const [page, setPage] = useState(0);
  const [playlistId, setPlaylistId] = useState<string | null>(initialPlaylistId);
  const [attaching, setAttaching] = useState<AttachableTrack | null>(null);

  useEffect(() => { const t = setTimeout(() => setSearch(text.trim()), 300); return () => clearTimeout(t); }, [text]);
  useEffect(() => { setPage(0); }, [search, genre, mood, tempo, musicType]);

  const options = useLibraryFilterOptions();
  const playlists = usePlaylists();
  const playlist = usePlaylist(playlistId);
  const tracks = useLibraryTracks(
    { genre: genre || undefined, mood: mood || undefined, tempo: tempo || undefined, music_type: musicType || undefined, search: search || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
    !playlistId,
  );

  const list = playlistId ? playlist.data?.tracks || [] : tracks.data?.tracks || [];
  const loading = playlistId ? playlist.isLoading : tracks.isLoading;
  const error = playlistId ? playlist.error : tracks.error;
  const total = tracks.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedPlaylist = playlists.data?.find((p) => p.playlist_id === playlistId);
  // Collapsed: the first few tracks of the current view, no pagination.
  const shown = expanded ? list : list.slice(0, PREVIEW_SIZE);
  const hiddenCount = playlistId ? list.length : total;
  const canExpand = !expanded && hiddenCount > shown.length;

  return (
    <section id="audio-library" aria-labelledby="audio-library-title" className="scroll-mt-6 space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          {/* Embedded in the Library tab the tab is the title; standalone the section names itself. */}
          <h2 id="audio-library-title" className={cn('flex items-center gap-2 text-[18px] text-[#101828]', !onExpandedChange && 'sr-only')} style={{ fontWeight: 700 }}>
            <Library className="h-5 w-5 text-[#667085]" aria-hidden /> Open library
          </h2>
          <p className="mt-0.5 text-[12.5px] text-[#667085]">{publicMode ? 'Pick a track for your video — play it here, then download it.' : 'Beatly’s curated catalog — every track cleared for your campaigns.'}</p>
        </div>
        {expanded && onExpandedChange && (
          <Button size="sm" variant="ghost" onClick={() => onExpandedChange(false)} className="h-8 shrink-0 gap-1 text-[12.5px] text-[#6941c6]">
            <ChevronUp className="h-4 w-4" aria-hidden /> Show less
          </Button>
        )}
      </div>

      {(playlists.data?.length ?? 0) > 0 && (
        <section aria-label="Featured collections">
          <h3 className="mb-3 text-[14px] text-[#101828]" style={{ fontWeight: 700 }}>Featured collections</h3>
          <PlaylistsRow playlists={playlists.data || []} selected={playlistId} onSelect={setPlaylistId} />
        </section>
      )}

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative min-w-0 flex-1 lg:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98a2b3]" aria-hidden />
          <input value={text} onChange={(e) => { setText(e.target.value); setPlaylistId(null); }} placeholder="Search tracks, artists, moods…" aria-label="Search the library" className="h-9 w-full rounded-[12px] border border-[#eaecf0] bg-white pl-9 pr-3 text-[13px] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[#98a2b3]/20" />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <select aria-label="Filter by genre" value={genre} onChange={(e) => { setGenre(e.target.value); setPlaylistId(null); }} className={selectCls}>
            <option value="">Any genre</option>
            {(options.data?.genres || []).map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select aria-label="Filter by mood" value={mood} onChange={(e) => { setMood(e.target.value); setPlaylistId(null); }} className={selectCls}>
            <option value="">Any mood</option>
            {(options.data?.moods || []).map((g) => <option key={g} value={g}>{capitalize(g)}</option>)}
          </select>
          <select aria-label="Filter by tempo" value={tempo} onChange={(e) => { setTempo(e.target.value); setPlaylistId(null); }} className={selectCls}>
            <option value="">Any tempo</option>
            {(options.data?.tempos || []).map((g) => <option key={g} value={g}>{capitalize(g)}</option>)}
          </select>
          <select aria-label="Filter by type" value={musicType} onChange={(e) => { setMusicType(e.target.value); setPlaylistId(null); }} className={selectCls}>
            <option value="">Any type</option>
            <option value="Instrumental">Instrumental</option>
            <option value="Singing">With vocals</option>
          </select>
        </div>
      </div>

      <section className="overflow-hidden rounded-[12px] border border-[#eaecf0] bg-white" aria-label="Tracks">
        {playlistId && (
          <div className="flex items-center gap-2 border-b border-[#f2f4f7] bg-[#f9fafb] px-4 py-2.5 text-[12.5px] text-[#344054]">
            <ListMusic className="h-4 w-4" aria-hidden />
            <span className="min-w-0 flex-1 truncate" style={{ fontWeight: 600 }}>{selectedPlaylist?.name || playlist.data?.name || 'Playlist'}</span>
            <button type="button" onClick={() => setPlaylistId(null)} className="flex items-center gap-1 rounded-md px-2 py-0.5 hover:bg-white" aria-label="Back to all tracks"><X className="h-3.5 w-3.5" aria-hidden /> All tracks</button>
          </div>
        )}
        <div className="hidden items-center gap-3 border-b border-[#f2f4f7] px-4 py-2 text-[11.5px] text-[#98a2b3] md:flex" style={{ fontWeight: 500 }}>
          <span className="w-12 shrink-0" />
          <span className="flex-1">Track</span>
          <span className="w-28">Genre</span>
          <span className="hidden w-28 lg:block">Mood</span>
          <span className="hidden w-16 lg:block">Tempo</span>
          <span className="w-12 text-right">Time</span>
          <span className="w-[150px] text-right">Actions</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-[#667085]" role="status"><Loader2 className="h-4 w-4 animate-spin text-[#98a2b3]" aria-hidden /> Loading tracks from the library…</div>
        ) : error ? (
          <div className="py-16 text-center text-[13px] text-[#b42318]" role="alert">Failed to load tracks. Please try again.</div>
        ) : list.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-[13px] text-[#667085]">No tracks found matching your criteria</p>
            <p className="mt-1 text-[12px] text-[#98a2b3]">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#f2f4f7]">
            {shown.map((t) => <LibraryTrackRow key={libraryTrackKey(t)} t={t} onAttach={publicMode ? undefined : setAttaching} linkTitle={publicMode} />)}
          </ul>
        )}
        {canExpand && !loading && !error && (
          <button type="button" onClick={() => onExpandedChange?.(true)} className="flex w-full items-center justify-center gap-1.5 border-t border-[#f2f4f7] px-4 py-3 text-[13px] text-[#6941c6] hover:bg-[#f9fafb]" style={{ fontWeight: 600 }}>
            <ChevronDown className="h-4 w-4" aria-hidden /> Browse all {hiddenCount} tracks
          </button>
        )}
        {expanded && !playlistId && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-[#f2f4f7] px-4 py-2.5 text-[12px] text-[#667085]">
            <span>Page {page + 1} of {pages} · {total} tracks</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-8" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous page"><ChevronLeft className="h-4 w-4" aria-hidden /></Button>
              <Button size="sm" variant="outline" className="h-8" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} aria-label="Next page"><ChevronRight className="h-4 w-4" aria-hidden /></Button>
            </div>
          </div>
        )}
      </section>

      {!publicMode && <AttachToCampaignDialog track={attaching} open={!!attaching} onOpenChange={(o) => !o && setAttaching(null)} />}
    </section>
  );
}
